import { useMemo } from 'react';

import { Button, ButtonProps, CircularProgress, IconButton, Tooltip } from '@mui/material';

import { useWindowSize } from '../../hooks';
import { SquareButton } from './SquareButton';

interface ResponsiveIconButtonProps extends ButtonProps {
  message: string;
  icon: React.ReactElement;
  breakpoint?: number;
  loading?: boolean;
  isSquare?: boolean;
  responsiveMessage?: string;
  keepColor?: boolean;
}

// A responsive button that can change from text to icon depending on the screen size
export function ResponsiveIconButton({
  message,
  variant = 'contained',
  color = 'primary',
  icon,
  endIcon,
  responsiveMessage = '',
  isSquare = false,
  startIcon,
  sx,
  onClick,
  disabled = false,
  breakpoint,
  loading = false,
  keepColor = false,
}: ResponsiveIconButtonProps) {
  const { width } = useWindowSize();

  const crossesBreakpoint = useMemo(() => {
    return width < (breakpoint ?? 480);
  }, [width, breakpoint]);

  if (crossesBreakpoint && isSquare)
    return (
      <SquareButton
        color={color}
        icon={icon ?? <></>}
        onClick={onClick}
        tooltip={message}
        keepColor={keepColor}
      />
    );

  if (crossesBreakpoint && !responsiveMessage)
    return (
      <Tooltip
        title={message}
        aria-label={message}
        arrow
      >
        <IconButton
          color={color}
          size='small'
          onClick={onClick}
          disabled={disabled || loading}
        >
          {icon}
        </IconButton>
      </Tooltip>
    );

  return (
    <Button
      sx={sx}
      variant={variant}
      color={color}
      endIcon={endIcon ?? <></>}
      startIcon={startIcon ?? <></>}
      onClick={onClick}
      disableElevation
      disabled={disabled || loading}
    >
      {loading ? (
        <CircularProgress
          size={24}
          color='inherit'
        />
      ) : crossesBreakpoint ? (
        responsiveMessage
      ) : (
        message
      )}
    </Button>
  );
}
